import Box from "@mui/material/Box";
import ArgonTypography from "components/ArgonTypography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PRIMARY = "#1E86F6";
const PRIMARY_SOFT = "rgba(30, 134, 246, 0.12)";
const TEXT = "#344767";
const MUTED = "#8392ab";
const BORDER = "#e9ecef";

const PORTAL_Z = 20000;

function HistorialAccionesSelect({
  id,
  value,
  onChange,
  options,
  emptyLabel,
  placeholder,
}) {
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const displayLabel = useMemo(() => {
    if (value === "" || value == null) return "";
    const found = options.find((o) => String(o.value) === String(value));
    return found ? found.label : String(value);
  }, [value, options]);

  const updateMenuPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({
      top: r.bottom + 8,
      left: r.left,
      width: Math.max(r.width, 200),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (anchorRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (v) => {
    onChange(v === "" || v == null ? "" : String(v));
    setOpen(false);
  };

  const allOptions = useMemo(
    () => [{ value: "", label: emptyLabel }, ...options],
    [options, emptyLabel]
  );

  const menu = open ? (
    <Box
      ref={menuRef}
      id={`${id}-listbox`}
      role="listbox"
      sx={{
        position: "fixed",
        top: menuPos.top,
        left: menuPos.left,
        width: menuPos.width,
        zIndex: PORTAL_Z,
        maxHeight: "min(280px, calc(100vh - 16px))",
        overflowY: "auto",
        py: 0.5,
        borderRadius: "14px",
        backgroundColor: "#fff",
        border: `1px solid ${PRIMARY_SOFT}`,
        boxShadow: "0 18px 48px -12px rgba(52, 71, 103, 0.35)",
      }}
    >
      {allOptions.map((opt) => {
        const selected = String(opt.value) === String(value);
        return (
          <Box
            key={opt.value === "" ? "__empty__" : String(opt.value)}
            component="button"
            type="button"
            role="option"
            aria-selected={selected}
            onClick={() => pick(opt.value)}
            sx={{
              display: "block",
              width: "100%",
              border: "none",
              borderLeft: selected ? `3px solid ${PRIMARY}` : "3px solid transparent",
              backgroundColor: selected ? "#e8f4fe" : "#fff",
              backgroundImage: "none",
              color: selected ? PRIMARY : TEXT,
              fontWeight: selected ? 700 : 500,
              fontSize: "0.8125rem",
              fontFamily: "inherit",
              textAlign: "left",
              px: 1.75,
              py: 1.15,
              cursor: "pointer",
              WebkitAppearance: "none",
              transition: "background-color 0.12s ease, border-color 0.12s ease",
              "&:hover": {
                backgroundColor: selected ? "#dceefd" : "#f0f7ff",
                color: selected ? PRIMARY : TEXT,
              },
            }}
          >
            {opt.label}
          </Box>
        );
      })}
    </Box>
  ) : null;

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        ref={anchorRef}
        component="button"
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
          fontSize: "0.875rem",
          fontWeight: 500,
          color: displayLabel ? TEXT : MUTED,
          backgroundColor: "#fff",
          backgroundImage: "none",
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          px: 1.75,
          py: 1.25,
          cursor: "pointer",
          textAlign: "left",
          outline: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.15s ease",
          "&:hover": {
            borderColor: "#dee2e6",
            backgroundColor: "#fafcff",
          },
          ...(open
            ? {
                borderColor: PRIMARY,
                boxShadow: `0 0 0 3px ${PRIMARY_SOFT}`,
                backgroundColor: "#fff",
              }
            : {}),
        }}
      >
        <ArgonTypography
          component="span"
          sx={{
            flex: 1,
            fontSize: "inherit",
            fontWeight: "inherit",
            color: "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {displayLabel || placeholder}
        </ArgonTypography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 22,
            color: MUTED,
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>

      {typeof document !== "undefined" && menu
        ? createPortal(menu, document.body)
        : null}
    </Box>
  );
}

HistorialAccionesSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  emptyLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
};

HistorialAccionesSelect.defaultProps = {
  placeholder: "",
};

export default HistorialAccionesSelect;
